import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCookieConsent } from '../hooks/useCookieConsent';
import './CookieBanner.css';

/**
 * Cookie Banner Component
 * GDPR-compliant cookie consent banner with expandable preferences
 */
const CookieBanner = () => {
  const {
    consent,
    showBanner,
    showExpanded,
    isLoading,
    acceptAll,
    rejectAll,
    saveCustomConsent,
    toggleExpanded,
    closeBanner,
  } = useCookieConsent();

  // Toggle states for custom preferences
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(true);

  // Sync toggle states with existing consent when re-accessing settings
  useEffect(() => {
    if (consent) {
      setAnalyticsEnabled(consent.analytics ?? true);
      setMarketingEnabled(consent.marketing ?? true);
    }
  }, [consent, showBanner]);

  // Create a persistent portal container so HeadlessUI Dialog won't
  // mark the cookie banner as `inert` when a modal is open.
  const portalRef = useRef(null);
  const [portalReady, setPortalReady] = useState(false);
  useEffect(() => {
    const el = document.createElement('div');
    el.setAttribute('data-headlessui-portal', '');
    el.id = 'cookie-banner-portal';
    document.body.appendChild(el);
    portalRef.current = el;
    setPortalReady(true);
    return () => {
      document.body.removeChild(el);
      portalRef.current = null;
    };
  }, []);

  // Don't render while loading or if banner shouldn't show
  if (isLoading || !showBanner || !portalReady) {
    return null;
  }

  // Handle save preferences
  const handleSavePreferences = () => {
    saveCustomConsent(analyticsEnabled, marketingEnabled);
  };

  return createPortal(
    <>
      {/* Backdrop Overlay */}
      <div 
        className="cookie-backdrop" 
        onClick={consent ? closeBanner : undefined}
      />

      {/* Banner */}
      <div className={`cookie-banner ${showExpanded ? 'cookie-banner-expanded' : ''}`}>
        {/* Close button (only show if user has already consented and is re-accessing) */}
        {consent && (
          <button 
            className="cookie-close-btn"
            onClick={closeBanner}
            aria-label="Close cookie settings"
          >
            ×
          </button>
        )}

        {/* Cookie Icon and Text */}
        <div className="cookie-header">
          <span className="cookie-icon">🍪</span>
        </div>

        <div className="cookie-content">
          <p className="cookie-text">
            We use cookies to improve your experience and show you relevant content. 
            Accept all to continue, or manage your preferences.{' '}
            <button 
              className="cookie-reject-inline" 
              onClick={rejectAll}
            >
              Reject all
            </button>
          </p>
        </div>

        {!showExpanded ? (
          /* Default View: Accept All, Manage - No Reject button here */
          <div className="cookie-actions">
            <button 
              className="cookie-accept-btn"
              onClick={acceptAll}
            >
              Accept All
            </button>
            <button 
              className="cookie-manage-btn"
              onClick={toggleExpanded}
            >
              Manage Preferences
            </button>
          </div>
        ) : (
          /* Expanded View: Category Toggles */
          <div className="cookie-preferences">
            <div className="cookie-divider" />
            
            {/* Essential Cookies - Always On */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <span className="cookie-category-icon">☑️</span>
                  <span className="cookie-category-name">Essential Cookies</span>
                </div>
                <span className="cookie-always-on">Always On</span>
              </div>
              <p className="cookie-category-desc">
                Required for the website to function properly.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <span className="cookie-category-icon">📊</span>
                  <span className="cookie-category-name">Analytics Cookies</span>
                </div>
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  />
                  <span className="cookie-toggle-slider" />
                </label>
              </div>
              <p className="cookie-category-desc">
                Help us understand how you use our platform.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="cookie-category">
              <div className="cookie-category-header">
                <div className="cookie-category-info">
                  <span className="cookie-category-icon">📢</span>
                  <span className="cookie-category-name">Marketing Cookies</span>
                </div>
                <label className="cookie-toggle">
                  <input
                    type="checkbox"
                    checked={marketingEnabled}
                    onChange={(e) => setMarketingEnabled(e.target.checked)}
                  />
                  <span className="cookie-toggle-slider" />
                </label>
              </div>
              <p className="cookie-category-desc">
                Used to show you relevant ads and measure campaigns.
              </p>
            </div>

            <div className="cookie-divider" />

            {/* Expanded Actions */}
            <div className="cookie-actions cookie-actions-expanded">
              <button 
                className="cookie-accept-btn"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </>,
    portalRef.current
  );
};

export default CookieBanner;
